// web/src/components/EfficiencyScatter.tsx
// Pure-SVG scatter. X = window spend ($, log scale).
// Y = cpl_vs_avg_pct (linear, centered on 0). Bubble color = verb (or grey).

import type { Verb } from "./VerbBadge";

export interface ScatterRow {
  adset_id: string;
  adset_name: string | null;
  spend: number;
  cpl_vs_avg_pct: number | null;       // null = no leads, can't plot vertically
  verb_or_null: Verb | null;
}

export interface EfficiencyScatterProps {
  rows: ScatterRow[];
  width?: number;
  height?: number;
}

const COLOR: Record<Verb | "maintain", string> = {
  scale: "#5fb37a",
  throttle: "#d9a64a",
  refresh_creative: "#5b9be8",
  kill: "#d96057",
  maintain: "#7a808c",
};

export function EfficiencyScatter({ rows, width = 360, height = 220 }: EfficiencyScatterProps) {
  // Skip rows with null cpl_vs_avg_pct — they have no Y coordinate to plot.
  const plottable = rows.filter((r) => r.cpl_vs_avg_pct !== null) as Array<ScatterRow & { cpl_vs_avg_pct: number }>;

  if (plottable.length === 0) {
    return (
      <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#7a808c", fontSize: 12 }}>
        No ad-set data in this window.
      </div>
    );
  }

  const pad = { l: 40, r: 12, t: 12, b: 30 };
  const innerW = width - pad.l - pad.r;
  const innerH = height - pad.t - pad.b;

  const xMin = Math.log10(Math.max(50, Math.min(...plottable.map((r) => r.spend))));
  const xMax = Math.log10(Math.max(50, Math.max(...plottable.map((r) => r.spend))));
  const xRange = Math.max(0.5, xMax - xMin);

  const yAbsMax = Math.max(50, ...plottable.map((r) => Math.abs(r.cpl_vs_avg_pct)));
  const yMin = -yAbsMax;
  const yMax = +yAbsMax;
  const yRange = yMax - yMin;

  const sizeMin = Math.min(...plottable.map((r) => r.spend));
  const sizeMax = Math.max(...plottable.map((r) => r.spend));

  function x(spend: number): number {
    const v = Math.log10(Math.max(50, spend));
    return pad.l + ((v - xMin) / xRange) * innerW;
  }
  function y(pct: number): number {
    return pad.t + ((yMax - pct) / yRange) * innerH;
  }
  function r(spend: number): number {
    const t = (spend - sizeMin) / Math.max(1, sizeMax - sizeMin);
    return 5 + t * 9; // 5..14 px
  }

  const yAvg = y(0);

  return (
    <svg width={width} height={height} role="img" aria-label="Ad-set efficiency vs spend">
      <line x1={pad.l} y1={pad.t} x2={pad.l} y2={pad.t + innerH} stroke="#2e3340" />
      <line x1={pad.l} y1={pad.t + innerH} x2={pad.l + innerW} y2={pad.t + innerH} stroke="#2e3340" />
      <line x1={pad.l} y1={yAvg} x2={pad.l + innerW} y2={yAvg}
            stroke="#4a5060" strokeDasharray="4 4" />
      {plottable.map((row) => {
        const color = COLOR[row.verb_or_null ?? "maintain"];
        return (
          <circle key={row.adset_id}
            cx={x(row.spend)} cy={y(row.cpl_vs_avg_pct)} r={r(row.spend)}
            fill={color} fillOpacity={0.7} stroke={color}>
            <title>
              {row.adset_name ?? "Unnamed"} — spend ${Math.round(row.spend)}
              {", "}CPL {row.cpl_vs_avg_pct >= 0 ? "+" : ""}{Math.round(row.cpl_vs_avg_pct)}% vs avg
              {row.verb_or_null ? ` · ${row.verb_or_null}` : ""}
            </title>
          </circle>
        );
      })}
      <text x={pad.l + innerW / 2} y={height - 8} fill="#7a808c" fontSize={10} textAnchor="middle">
        Spend ($, log) →
      </text>
      <text x={10} y={pad.t + innerH / 2} fill="#7a808c" fontSize={10}
            transform={`rotate(-90 10 ${pad.t + innerH / 2})`} textAnchor="middle">
        CPL vs account avg (%) ↑
      </text>
    </svg>
  );
}
